import { useDispatch, useSelector } from "react-redux";
import { addToCart, removeFromCart, reset } from "../features/cart/cartSlice";
import { Link } from "react-router-dom";
import Loader from "./Loader";
import { useEffect, useState } from "react";
import { useToast } from "./Toast";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

function Product({ product }) {
  const toast = useToast();
  const [isCurrentlyAdding, setIsCurrentlyAdding] = useState(false);
  const [isCurrentlyRemoving, setIsCurrentlyRemoving] = useState(false);
  const [cartItem, setCartItem] = useState([]);
  const [quantity, setQuantity] = useState(0);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { user } = useSelector((state) => state.auth);

  useEffect(() => {
    if (user) {
      if (cartItem && cartItem.length > 0) {
        setQuantity(cartItem?.length);
      } else {
        setQuantity(0);
      }
    }
  }, [cartItem, user]);

  useEffect(() => {
    const cartItems = user?.data?.cart || [];
    const foundItems = cartItems.filter((item) => item.code === product.code);
    setCartItem(foundItems);
  }, [user, product]);

  const handleAddToCart = () => {
    if (!user || !user.data?.email) {
      setIsCurrentlyAdding(false);
      toast.info("Please log in to add items to your cart.");
      navigate("/login");
      return; // ← stop execution here, user is not logged in
    }
    setIsCurrentlyAdding(true);
    dispatch(addToCart({ email: user.data.email, item: product }))
      .unwrap()
      .finally(() => {
        setIsCurrentlyAdding(false);
      });
    dispatch(reset());
    return;
  };

  const handleIncrement = () => {
    if (!user || !user.data?.email) {
      toast.info("Please log in to add items to your cart.");
      navigate("/login");
      return;
    }
    setIsCurrentlyAdding(true);
    dispatch(addToCart({ email: user.data.email, item: product }))
      .unwrap()
      .finally(() => {
        setIsCurrentlyAdding(false);
      });
    dispatch(reset());
  };

  const handleDecrement = () => {
    if (!user || !user.data?.email) {
      toast.info("Please log in to update your cart.");
      navigate("/login");
      return;
    }
    setIsCurrentlyRemoving(true);
    dispatch(removeFromCart({ code: product.code, email: user.data.email }))
      .unwrap()
      .finally(() => {
        setIsCurrentlyRemoving(false);
      });
    dispatch(reset());
  };

  return (
    <motion.div
      className="flex flex-col items-center justify-between w-full h-full p-6 bg-white/5 backdrop-blur-md border border-white/10 rounded-[2rem] shadow-2xl relative group overflow-hidden"
      whileHover={{ y: -8, borderColor: "rgba(0,242,255,0.5)" }}
      transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
    >
      {/* Background Decor */}
      <motion.div
        className="absolute top-0 right-0 w-32 h-32 bg-cyan-500/5 rounded-full blur-[50px]"
        whileHover={{ scale: 1.2, opacity: 0.2 }}
        transition={{ duration: 0.3 }}
      ></motion.div>

      <div className="flex justify-between items-center w-full z-10">
        <Link
          to={`/product/${product.code}`}
          className="hover:text-cyan-400 transition-colors"
        >
          <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-widest leading-relaxed max-w-[150px]">
            {product.name || product.title}
          </h3>
        </Link>
        <span className="px-3 py-1 bg-cyan-500/10 border border-cyan-500/30 rounded-lg text-cyan-400 font-black text-[9px] uppercase tracking-tighter">
          Elite Grade
        </span>
      </div>

      <Link
        to={`/product/${product.code}`}
        className="flex flex-col items-center w-full py-8"
      >
        <motion.img
          src={product.image}
          alt={product.name}
          className="w-full h-48 sm:h-56 object-contain"
          whileHover={{ scale: 1.1 }}
          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          style={{ filter: "drop-shadow(0 0 20px rgba(0,242,255,0.2))" }}
        />
      </Link>

      <div className="flex justify-between items-center w-full mt-2 pt-6 border-t border-white/5 z-10">
        <div className="flex flex-col">
          <span className="text-[10px] font-black text-gray-500 uppercase tracking-widest mb-1">
            Market Value
          </span>
          <span className="text-xl font-black text-white italic">
            ${product.price?.toLocaleString()}
          </span>
        </div>

        <AnimatePresence mode="wait">
          {quantity > 0 ? (
            <motion.div
              key="quantity-controls"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.2 }}
              className="flex items-center gap-3 bg-black/40 p-1.5 rounded-2xl border border-white/10"
            >
              <motion.button
                onClick={handleDecrement}
                disabled={isCurrentlyRemoving || quantity === 0}
                whileHover={{ scale: 1.1, backgroundColor: "#ef4444", color: "#000" }}
                whileTap={{ scale: 0.9 }}
                className="w-8 h-8 bg-white/5 rounded-xl text-white transition-all disabled:opacity-50 flex items-center justify-center border border-white/5"
              >
                {isCurrentlyRemoving ? <Loader size="sm" /> : "-"}
              </motion.button>
              <motion.span
                key={quantity}
                initial={{ scale: 1.5, color: "#00f2ff" }}
                animate={{ scale: 1, color: "#00f2ff" }}
                transition={{ duration: 0.2 }}
                className="font-black text-cyan-400 text-xs w-4 text-center"
              >
                {quantity}
              </motion.span>
              <motion.button
                onClick={handleIncrement}
                disabled={isCurrentlyAdding}
                whileHover={{ scale: 1.1, backgroundColor: "#00f2ff", color: "#000" }}
                whileTap={{ scale: 0.9 }}
                className="w-8 h-8 bg-white/5 rounded-xl text-white transition-all disabled:opacity-50 flex items-center justify-center border border-white/5"
              >
                {isCurrentlyAdding ? <Loader size="sm" /> : "+"}
              </motion.button>
            </motion.div>
          ) : (
            <motion.button
              key="add-button"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.2 }}
              onClick={handleAddToCart}
              disabled={isCurrentlyAdding}
              whileHover={{ scale: 1.05, backgroundColor: "#00f2ff" }}
              whileTap={{ scale: 0.95 }}
              className="relative bg-white text-black px-6 py-3.5 rounded-2xl text-[10px] font-black uppercase tracking-[0.2em] disabled:opacity-50 shadow-[0_0_20px_rgba(255,255,255,0.05)] overflow-hidden"
            >
              <span className="relative z-10 flex items-center gap-2">
                {isCurrentlyAdding ? <Loader size="sm" /> : "Acquire Hardware"}
              </span>
            </motion.button>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}

export default Product;
